import {
    useCallback, useEffect, useRef, useState
} from 'react';
import replenishmentApi from 'services/api/productFrequency/updateReplenishSubscriptions';

const CONFIRM_THROTTLE_MS = 2000;

/**
 * Manages the Manage-Subscription change-quantity confirm flow: tracks the pending quantity,
 * toggles between the base and confirm views, and submits the update (throttled) via the
 * replenishment API, invoking onQtyUpdated on success.
 *
 * @param {Object} params
 * @param {Object} params.product - The subscription product being edited.
 * @param {Object} params.currentSubscription - The active subscription (provides subscriptionId).
 * @param {Function} params.onQtyUpdated - Called after a successful quantity update.
 * @returns {Object} View state and handlers (view, pendingQty, committedQty, isSubmitting, submitError, confirmButtonRef, onQtyChange, onCancel, onConfirm).
 */
export function useConfirmUpdateFlow({ product, currentSubscription, onQtyUpdated }) {
    const [view, setView] = useState('base');
    const [pendingQty, setPendingQty] = useState(product?.qty);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [committedQty, setCommittedQty] = useState(null);
    const lastConfirmRef = useRef(0);
    const confirmButtonRef = useRef(null);

    const currentQty = committedQty != null ? committedQty : product?.qty;

    const onQtyChange = useCallback(
        newQty => {
            if (newQty === currentQty) {
                return;
            }

            setPendingQty(newQty);
            setSubmitError(false);
            setView('confirm');
        },
        [currentQty]
    );

    const onCancel = useCallback(() => {
        setPendingQty(currentQty);
        setSubmitError(false);
        setView('base');
    }, [currentQty]);

    const onConfirm = useCallback(() => {
        const now = Date.now();

        if (isSubmitting || now - lastConfirmRef.current < CONFIRM_THROTTLE_MS) {
            return;
        }

        lastConfirmRef.current = now;
        setIsSubmitting(true);
        setSubmitError(false);

        replenishmentApi
            .updateReplenishSubscriptions({
                subscriptionId: currentSubscription.subscriptionId,
                items: [{ skuId: product.skuId, qty: pendingQty }]
            })
            .then(data => {
                if (data && (data.responseStatus >= 400 || data.errors)) {
                    setSubmitError(true);

                    return;
                }

                setCommittedQty(pendingQty);
                onQtyUpdated?.({ subscriptionId: currentSubscription.subscriptionId, skuId: product.skuId, qty: pendingQty, data });
                setView('base');
            })
            .catch(() => {
                setSubmitError(true);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [isSubmitting, currentSubscription, product.skuId, pendingQty, onQtyUpdated]);

    useEffect(() => {
        if (view === 'confirm') {
            confirmButtonRef.current?.focus();
        }
    }, [view]);

    return {
        view, pendingQty, committedQty, isSubmitting, submitError, confirmButtonRef, onQtyChange, onCancel, onConfirm
    };
}
